import { useState, useMemo, useEffect } from 'react';
import { B } from '../../constants/brand';
import { buildFolderTree } from '../../utils/docFolders';
import { Btn, Modal, Icon, useToast } from '../ui';

// FolderPlanModal — review the folder structure Claude generated for a deal,
// then organise SharePoint to match.
//
// The plan comes from the deal's own documents rather than a template, so the
// interesting thing to review isn't the folder names on their own — it's where
// each file ends up. The modal leads with the proposed tree, then lists every
// move with the file's current folder beside its new one, ticked by default
// only where Claude was confident. Applying is what touches live SharePoint, so
// nothing here happens until the button is pressed, and the button says exactly
// how many files it will move.

const Row = ({ children, style }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...style }}>{children}</div>
);

const Section = ({ title, sub, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.6,
      textTransform: 'uppercase', marginBottom: 6 }}>{title}</div>
    {sub && <div style={{ fontSize: 12, color: B.textLt, marginBottom: 8, lineHeight: 1.5 }}>{sub}</div>}
    {children}
  </div>
);

const TreeNode = ({ node, depth }) => (
  <div>
    <Row style={{ padding: `4px 8px 4px ${8 + depth * 16}px`, fontSize: 13,
      color: node.selectable ? B.text : B.textMd, fontWeight: node.selectable ? 500 : 700 }}>
      <span style={{ display: 'flex', color: node.selectable ? B.tealDk : B.textLt, flexShrink: 0 }}>
        <Icon name="folder" size={13} />
      </span>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {node.label || node.name}
      </span>
      {node.docCount > 0 && (
        <span style={{ fontSize: 11, color: B.textLt, flexShrink: 0 }}>
          {node.docCount} file{node.docCount === 1 ? '' : 's'}
        </span>
      )}
    </Row>
    {node.children.map(c => <TreeNode key={c.path} node={c} depth={depth + 1} />)}
  </div>
);

const Confidence = ({ value }) => {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 75 ? B.green : pct >= 50 ? '#8a6d1f' : B.textLt;
  return <span style={{ fontSize: 11, fontWeight: 600, color, flexShrink: 0 }}>{pct}%</span>;
};

export const FolderPlanModal = ({ dealName, plan: state, generating, applying, error, errorIsTimeout, onGenerate, onApply, onClose }) => {
  const { toast } = useToast();
  const [selected, setSelected] = useState(() => new Set());
  const [showStays, setShowStays] = useState(false);

  const plan = state?.plan || null;
  const assignments = plan?.assignments || [];
  const moves = useMemo(() => assignments.filter(a => !a.unchanged), [assignments]);
  const stays = useMemo(() => assignments.filter(a => a.unchanged), [assignments]);

  // Pre-tick only the confident moves — a low-confidence guess is something the
  // user opts into, not something they have to notice and untick.
  useEffect(() => {
    setSelected(new Set(moves.filter(m => m.autoMove).map(m => m.documentId)));
  }, [moves]);

  // The folder tree, with each folder's incoming file count, so the shape of
  // the result is visible at a glance rather than inferred from the move list.
  const tree = useMemo(() => {
    const counts = new Map();
    for (const a of assignments) counts.set(a.to, (counts.get(a.to) || 0) + 1);
    return buildFolderTree((plan?.folders || []).map(f => ({ ...f, docCount: counts.get(f.path) || 0 })));
  }, [plan, assignments]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const apply = async () => {
    try {
      const res = await onApply({ documentIds: Array.from(selected) });
      const bits = [
        `${res.foldersCreated} folder${res.foldersCreated === 1 ? '' : 's'} ready`,
        `${res.documentsMoved} file${res.documentsMoved === 1 ? '' : 's'} moved`,
      ];
      const failed = (res.moveFailures?.length || 0) + (res.folderFailures?.length || 0);
      if (failed) {
        toast.error(`${bits.join(', ')} — ${failed} failed. Check the logs and re-apply.`);
      } else if (!res.sharePointApplied) {
        toast.success(`${bits.join(', ')} in Harvi. SharePoint isn’t configured, so nothing moved there.`);
      } else {
        toast.success(bits.join(', '));
      }
      onClose();
    } catch (e) { toast.error(e.message); }
  };

  const status = state?.status || 'none';

  // A failed REQUEST (timeout, 400, 500) never reaches `state`, so it has to be
  // rendered from the error itself — otherwise the modal falls back to "no
  // structure generated yet" and the click looks like a no-op, which is exactly
  // how a silent 10s timeout used to present.
  const errorBanner = error ? (
    <div style={{ fontSize: 13, color: B.red, lineHeight: 1.6, background: `${B.red}0d`,
      border: `1px solid ${B.red}33`, borderRadius: 6, padding: '10px 12px', marginBottom: 16 }}>
      {error}
      <div style={{ color: B.textMd, marginTop: 6 }}>
        {errorIsTimeout
          // Generation runs inline and the server does not stop when the browser
          // gives up, so a timeout genuinely may still be working.
          ? 'Generating reads every document in this deal and calls Claude, so it can '
            + 'take a while. The server may still be finishing — reopen this in a moment '
            + 'and the structure should be here.'
          // Anything else already failed. Saying "it may still be running" here
          // would just send you back to wait for something that isn't coming.
          : 'This run has stopped — it isn’t still working in the background. Reopen '
            + 'this modal to see the recorded reason, or try again.'}
      </div>
    </div>
  ) : null;

  // A run in flight, as the server reports it. `pending` is authoritative over
  // the local `generating` flag: the run outlives the request that started it,
  // so this still shows progress after a page reload or a timed-out POST.
  const running = status === 'pending' || generating;
  const progress = state?.progress || null;
  const runningFor = state?.runningForMs;
  const busy = generating || applying || status === 'pending';

  const runBanner = running ? (
    <div style={{ background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 6,
      padding: '12px 14px', marginBottom: 16 }}>
      <Row style={{ justifyContent: 'space-between', marginBottom: progress?.index ? 8 : 0 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: B.text }}>
          {progress?.label || 'Reading this deal’s documents and designing a folder structure…'}
        </span>
        {progress?.index && progress?.total && (
          <span style={{ fontSize: 12, color: B.textMd, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            Step {progress.index} of {progress.total}
          </span>
        )}
      </Row>

      {progress?.index && progress?.total && (
        <div style={{ height: 5, borderRadius: 3, background: B.border, overflow: 'hidden' }}>
          <div style={{ width: `${Math.round((progress.index / progress.total) * 100)}%`,
            height: '100%', background: B.tealDk, transition: 'width .4s' }} />
        </div>
      )}

      <div style={{ fontSize: 11.5, color: B.textLt, marginTop: 8, lineHeight: 1.5 }}>
        {progress?.detail ? `${progress.detail}. ` : ''}
        {Number.isFinite(runningFor) && runningFor > 4000
          ? `Running for ${Math.round(runningFor / 1000)}s. `
          : ''}
        This keeps running on the server — you can close this and come back.
      </div>
    </div>
  ) : null;

  const body = () => {
    // A live run wins over everything: it's the most current thing we know, and
    // any error we might be holding predates it. A previous plan stays visible
    // underneath, so a regeneration never blanks the plan you already had.
    if (running) return (
      <>
        {runBanner}
        {plan && (
          <div style={{ fontSize: 12, color: B.textLt, lineHeight: 1.6 }}>
            Showing the previous structure until the new one is ready.
          </div>
        )}
      </>
    );
    if (error && !plan) return errorBanner;
    if (state?.available === false) return (
      <div style={{ fontSize: 13, color: B.textMd, lineHeight: 1.6 }}>
        Folder plans aren’t available yet — migration 147 hasn’t been run on this database.
      </div>
    );
    if (status === 'unavailable') return (
      <div style={{ fontSize: 13, color: B.textMd, lineHeight: 1.6 }}>
        No AI connection is configured for folder planning. Add one on <strong>Settings → AI Connections</strong>
        {' '}(feature: “Deal Folder Structure”), then generate again.
      </div>
    );
    if (status === 'empty') return (
      <div style={{ fontSize: 13, color: B.textMd, lineHeight: 1.6 }}>
        This deal has no documents yet. The structure is generated from what the deal holds, so upload some
        files first — then come back here.
      </div>
    );
    if (status === 'error') return (
      <div style={{ fontSize: 13, color: B.red, lineHeight: 1.6 }}>
        {state?.message || 'The folder structure couldn’t be generated.'}
      </div>
    );
    if (status === 'none' || !plan) return (
      <div style={{ fontSize: 13, color: B.textMd, lineHeight: 1.6 }}>
        No structure has been generated for this deal yet. Claude will read its
        {state?.documentCount ? ` ${state.documentCount}` : ''} document
        {state?.documentCount === 1 ? '' : 's'} and propose folders that fit them.
      </div>
    );

    return (
      <>
        {errorBanner}
        {plan.rationale && (
          <div style={{ fontSize: 13, color: B.text, lineHeight: 1.6, background: B.surfaceMuted,
            borderRadius: 6, padding: '10px 12px', marginBottom: 16 }}>
            {plan.rationale}
          </div>
        )}

        {state?.stale && (
          <div style={{ fontSize: 12, color: '#8a6d1f', background: '#f7f2e4', borderRadius: 6,
            padding: '8px 12px', marginBottom: 16, lineHeight: 1.5 }}>
            This plan was generated before the deal’s documents last changed. Regenerate for an up-to-date structure.
          </div>
        )}

        <Section title="Proposed structure"
          sub={`${plan.folders.length} folder${plan.folders.length === 1 ? '' : 's'}, derived from this deal’s documents.`}>
          <div style={{ border: `1px solid ${B.border}`, borderRadius: 6, padding: '6px 0', maxHeight: 240, overflowY: 'auto' }}>
            {tree.map(n => <TreeNode key={n.path} node={n} depth={0} />)}
          </div>
        </Section>

        <Section title={`Files to move (${moves.length})`}
          sub={moves.length
            ? 'Ticked files are moved in SharePoint and in Harvi. Anything Claude was unsure about is left unticked for you to decide.'
            : 'Nothing needs moving — every document is already where the plan puts it.'}>
          {moves.length > 0 && (
            <>
              <Row style={{ marginBottom: 6 }}>
                <span onClick={() => setSelected(new Set(moves.map(m => m.documentId)))}
                  style={{ fontSize: 12, color: B.tealDk, cursor: 'pointer', fontWeight: 600 }}>Select all</span>
                <span style={{ color: B.border }}>|</span>
                <span onClick={() => setSelected(new Set())}
                  style={{ fontSize: 12, color: B.tealDk, cursor: 'pointer', fontWeight: 600 }}>Select none</span>
              </Row>
              <div style={{ border: `1px solid ${B.border}`, borderRadius: 6, maxHeight: 260, overflowY: 'auto' }}>
                {moves.map(m => (
                  <label key={m.documentId}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px',
                      borderBottom: `1px solid ${B.border}`, cursor: 'pointer',
                      background: selected.has(m.documentId) ? B.tealLt : B.white }}>
                    <input type="checkbox" checked={selected.has(m.documentId)}
                      onChange={() => toggle(m.documentId)} style={{ marginTop: 3 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: B.text,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.fileName}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color: B.textMd, marginTop: 2 }}>
                        {m.from || '(deal root)'} <span style={{ color: B.textLt }}>→</span> <strong>{m.to}</strong>
                      </span>
                    </span>
                    <Confidence value={m.confidence} />
                  </label>
                ))}
              </div>
            </>
          )}
        </Section>

        {stays.length > 0 && (
          <Section title={`Already in place (${stays.length})`}>
            <span onClick={() => setShowStays(v => !v)}
              style={{ fontSize: 12, color: B.tealDk, cursor: 'pointer', fontWeight: 600 }}>
              {showStays ? 'Hide' : 'Show'}
            </span>
            {showStays && (
              <div style={{ border: `1px solid ${B.border}`, borderRadius: 6, marginTop: 6, maxHeight: 180, overflowY: 'auto' }}>
                {stays.map(s => (
                  <div key={s.documentId} style={{ padding: '6px 10px', borderBottom: `1px solid ${B.border}`, fontSize: 12.5 }}>
                    <span style={{ color: B.text }}>{s.fileName}</span>
                    <span style={{ color: B.textLt }}> — {s.to || '(deal root)'}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {plan.unassigned?.length > 0 && (
          <Section title={`Left where they are (${plan.unassigned.length})`}
            sub="Claude didn’t place these. They stay in their current folder — file them by hand, or regenerate.">
            <div style={{ border: `1px solid ${B.border}`, borderRadius: 6, maxHeight: 140, overflowY: 'auto' }}>
              {plan.unassigned.map(u => (
                <div key={u.documentId} style={{ padding: '6px 10px', borderBottom: `1px solid ${B.border}`, fontSize: 12.5, color: B.text }}>
                  {u.fileName}
                </div>
              ))}
            </div>
          </Section>
        )}

        {state?.message && (
          <div style={{ fontSize: 11.5, color: B.textLt, lineHeight: 1.5 }}>{state.message}</div>
        )}
      </>
    );
  };

  const canApply = status === 'ready' && plan && !busy;

  return (
    <Modal title={`Folder structure — ${dealName || 'this deal'}`} width={720} onClose={onClose}>
      <div style={{ fontSize: 12.5, color: B.textMd, lineHeight: 1.6, marginBottom: 16 }}>
        Claude reads this deal’s documents and designs the folders that fit them. Applying the plan creates those
        folders in SharePoint and moves the files — Harvi and SharePoint end up organised the same way.
      </div>

      {body()}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
        <Btn onClick={apply} disabled={!canApply || (moves.length > 0 && selected.size === 0 && !plan?.folders?.length)}>
          {applying
            ? 'Organising…'
            : selected.size
              ? `Create folders & move ${selected.size} file${selected.size === 1 ? '' : 's'}`
              : 'Create folders in SharePoint'}
        </Btn>
        <Btn variant="secondary" onClick={() => { onGenerate({ force: true }).catch(() => {}); }} disabled={busy}>
          {/* `running`, not `generating` — a run that outlived its request (or
              was started in another tab) still reads as in-flight here. */}
          {running ? 'Generating…' : plan ? 'Regenerate' : 'Generate structure'}
        </Btn>
        <div style={{ flex: 1 }} />
        <Btn variant="secondary" onClick={onClose} disabled={applying}>Close</Btn>
      </div>
    </Modal>
  );
};
